import React from 'react';
import { ShoppingBag, CheckCircle, Shield, Award, Zap, Users, Clock, Lock, TrendingUp, FileText, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import PopupButton from '../components/PopupButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import TestimonialSlider from '../components/TestimonialSlider';
import HowItWorksSlider from '../components/HowItWorksSlider';
import WhyYouNeedItSlider from '../components/WhyYouNeedItSlider';
import WhyChooseUsSlider from '../components/WhyChooseUsSlider';
import BenefitsSlider from '../components/BenefitsSlider';
import { amazonBrandPricing, amazonTestimonials } from '../data/mock';
import SEO from '../components/SEO';

const AmazonBrandRegistry = () => {
  const processSteps = [
    {
      step: 1,
      title: 'Verify Eligibility',
      description: 'Ensure your trademark is registered or pending with the USPTO.',
      icon: FileText
    },
    {
      step: 2,
      title: 'Submit Application',
      description: 'We prepare and submit your Brand Registry application to Amazon.',
      icon: ShoppingBag
    },
    {
      step: 3,
      title: 'Get Enrolled',
      description: 'Gain access to powerful brand protection and marketing tools.',
      icon: Award
    }
  ];

  const whyYouNeedIt = [
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Powerful tools to protect your intellectual property on Amazon.'
    },
    {
      icon: TrendingUp,
      title: 'Enhanced Content',
      description: 'Create rich product pages with A+ Content and Brand Stores.'
    },
    {
      icon: Lock,
      title: 'Stop Counterfeits',
      description: 'Automated protections to remove fake and counterfeit listings.'
    },
    {
      icon: Award,
      title: 'Boost Sales',
      description: 'Increase conversions with better product presentation.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Zap,
      title: 'Fast Enrollment',
      description: 'Get enrolled in Amazon Brand Registry quickly'
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Amazon Brand Registry specialists help you'
    },
    {
      icon: CheckCircle,
      title: '100% Success',
      description: 'We ensure your application is approved'
    },
    {
      icon: Clock,
      title: 'Full Guidance',
      description: 'Step-by-step assistance throughout'
    }
  ];

  const benefits = [
    {
      title: 'Proactive Brand Protection',
      description: 'Amazon monitors and automatically removes suspected counterfeit listings.'
    },
    {
      title: 'Enhanced Brand Content',
      description: 'Access A+ Content to create rich product descriptions with images and videos.'
    },
    {
      title: 'Brand Analytics',
      description: 'Get insights into customer search terms and purchasing behavior.'
    },
    {
      title: 'Sponsored Brands Ads',
      description: 'Run branded advertising campaigns to increase visibility and sales.'
    },
    {
      title: 'Amazon Stores',
      description: 'Create a multi-page branded storefront to showcase your product catalog.'
    },
    {
      title: 'Report Violations',
      description: 'Quickly report and remove infringing content and counterfeit products.'
    }
  ];

  const faqs = [
    {
      id: 1,
      question: 'What are the requirements for Amazon Brand Registry?',
      answer: 'You need a registered trademark with a government trademark office (like USPTO). Text-based marks or image-based marks with words are eligible. Image-only marks are not eligible.'
    },
    {
      id: 2,
      question: 'Can I enroll with a pending trademark?',
      answer: 'Yes, you can enroll with a pending trademark application. However, having a registered trademark provides the full benefits of Amazon Brand Registry.'
    },
    {
      id: 3,
      question: 'How long does enrollment take?',
      answer: 'Once you submit your application with all required information, Amazon typically processes it within 1-2 weeks. We help ensure your application is complete to avoid delays.'
    },
    {
      id: 4,
      question: 'What information does Amazon need?',
      answer: 'You will need your trademark registration number, the countries where your trademark is registered, product categories, and images of your products showing the trademark.'
    },
    {
      id: 5,
      question: 'Can I protect multiple brands?',
      answer: 'Yes, you can enroll multiple brands in Amazon Brand Registry. Each brand requires its own registered trademark.'
    },
    {
      id: 6,
      question: 'What happens after enrollment?',
      answer: 'You gain immediate access to brand protection tools, A+ Content, Brand Analytics, Sponsored Brands ads, and the ability to create an Amazon Store for your brand.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Amazon Brand Registry Services | Assess Trademark"
        description="Enroll your business in Amazon Brand Registry with expert assistance. Protect your products, improve brand control, and strengthen your Amazon presence."
      />
      {/* Banner */}
      <section className="py-12 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">Amazon Brand Registry</h1>
        </div>
      </section>

      {/* 1. About the Service */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Amazon Brand Registry
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Get enrolled in Amazon's Brand Registry to protect your products and boost sales. Access powerful tools including A+ Content, Brand Analytics, and enhanced brand protection on the world's largest marketplace.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Starting at just $99, we handle your entire enrollment process and ensure you're approved quickly. Protect your brand and grow your Amazon business today.
              </p>
              <PopupButton size="lg" className="bg-secondary hover:bg-secondary/90 text-white">
                Get Started Now
              </PopupButton>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-xl flex items-center justify-center bg-blue-50 p-4">
                <img 
                  src="/assets/img/Amazon-Brand-Registry.svg" 
                  alt="Amazon Brand Registry Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Simple enrollment process for Amazon sellers</p>
          </div>
          <HowItWorksSlider items={processSteps} />
        </div>
      </section>

      {/* 3. Why You Need It */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why You Need Amazon Brand Registry</h2>
            <p className="text-lg text-gray-600">Essential protection and growth tools for Amazon sellers</p>
          </div>
          <WhyYouNeedItSlider items={whyYouNeedIt} />
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
            <p className="text-lg text-gray-600">Affordable enrollment packages for every seller</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {amazonBrandPricing.map((plan) => (
              <Card key={plan.id} className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? 'ring-2 ring-primary scale-105' : ''}`}>
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-secondary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">+ USPTO filing fee</p>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <PopupButton className="w-full bg-secondary hover:bg-secondary/90 text-white">
                    Select Plan
                  </PopupButton>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Assess Trademark</h2>
            <p className="text-lg text-gray-600">Expert Amazon Brand Registry enrollment</p>
          </div>
          <WhyChooseUsSlider items={whyChooseUs} />
        </div>
      </section>

      {/* 6. Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600">What you get with Amazon Brand Registry</p>
          </div>
          <BenefitsSlider benefits={benefits} />
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600">Trusted by Amazon sellers nationwide</p>
          </div>
          <TestimonialSlider testimonials={amazonTestimonials} />
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about Amazon Brand Registry</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold hover:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Protect Your Amazon Brand?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Enroll in Amazon Brand Registry today and unlock powerful protection tools.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/contact-us">
              <PopupButton size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100">
                Get Started Now
              </PopupButton>
            </Link>
            <Link to="/contact-us">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandRegistry;
